import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';

/**
 * Owner 2026-10-24 10:53 („zapytaj czy przejść do edycji pozycji w katalogu”): Ctrl+klik (Windows: Alt+klik) on a line
 * first asks „Przejść do edycji „<pozycja>” w katalogu?” — [Przejdź] / [Anuluj], Esc is „Anuluj”. One dialog for the
 * whole app (in `AppLayout`), opened by `askOpenInCatalog` from the shared opener (`useCatalogOpener`).
 */

type Ask = { name: string; go: () => void };
let show: ((a: Ask) => void) | null = null;

/** Asks before `go`; with no host mounted (a screen outside the app's layout) it just goes. */
export function askOpenInCatalog(name: string, go: () => void): void {
  if (show) show({ name, go });
  else go();
}

export function CatalogOpenHost() {
  const [pending, setPending] = useState<Ask | null>(null);
  useLayoutEffect(() => {
    show = setPending;
    return () => {
      if (show === setPending) show = null;
    };
  }, []);
  // Focus „Przejdź” only after the gesture is over: the mouse press that opened it (Safari's Ctrl+click sends
  // `contextmenu` on the press) would otherwise put the focus back on the row, and Esc / Enter would go there.
  const primary = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!pending) return;
    const t = window.setTimeout(() => primary.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [pending]);
  if (!pending) return null;
  const cancel = () => setPending(null);
  const go = () => {
    setPending(null);
    pending.go();
  };
  return createPortal(
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="catalog-open-title"
        data-feature-id="catalog.open_from_line"
        onKeyDown={(e) => {
          // Esc is „Anuluj” here first: a layer opened under it meanwhile (the quote's selection) must not take it
          if (e.key === 'Escape' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            e.stopPropagation();
            cancel();
            return;
          }
          if (e.key !== 'Tab') return;
          trapTab(e.currentTarget, e.nativeEvent);
          e.stopPropagation();
        }}
      >
        <DialogLayer onClose={cancel} returnFocus={false} />
        <h2 id="catalog-open-title">Przejść do edycji „{pending.name}” w katalogu?</h2>
        <div className="actions">
          <button ref={primary} className="btn primary" type="button" autoFocus onClick={go}>
            Przejdź
          </button>
          <button className="btn ghost" type="button" onClick={cancel}>
            Anuluj
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
